import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { Panes } from 'modules/core/components/Panes'
import { PanesPad } from 'modules/core/components/PanesPad'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'

import type { Store as AutomationsStore } from 'modules/automations/lib/Store'
import { automationTranslations } from 'modules/automations/lib/automationTranslations'
import { actions } from 'modules/automations/store/collections/actions'
import { Action } from 'modules/automations/store/models/Action'
import type { Automation } from 'modules/automations/store/models/Automation'
import type { ITriggerWithConditions } from 'modules/automations/store/models/Trigger'
import CustomFieldsCollection from 'modules/custom_resources/store/collections/customFields/fields'

import { BranchAction } from './Branch'
import { CachedDataProvider } from './Providers/CachedDataProvider'
import { RequestReviewAction } from './RequestReviewAction'
import { SendCustomNotificationAction } from './SendCustomNotification'
import { SendNotificationAction } from './SendNotification'
import TimeAction from './Time'
import { Title } from './Title'
import { AutomationTrigger } from './Trigger'
import styles from './index.module.css'

interface Props {
  automationsStore: AutomationsStore
  automation: Automation
  trigger?: ITriggerWithConditions
}
type ActionsSwitchProps = Props & {
  action: Action
  index: number
  actionsArray: Action[]
  loadingCustomFields: boolean
  customFieldsCollection: CustomFieldsCollection
}
const ActionSwitch = ({
  automation,
  automationsStore,
  action,
  index,
  trigger,
  actionsArray,
  loadingCustomFields,
  customFieldsCollection,
}: ActionsSwitchProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const id = action.attributes.id
  const isLast = index === actionsArray.length - 1

  const props = {
    action: action,
    form: automationsStore.forms[id],
    onClick: () => {
      navigate(
        navigation.settings.automations.id.details.addBlockParentId.fullPath({
          id: automation.id,
          parentId: id,
        })
      )
    },
    withLastSpacer: !isLast,
    loadingCustomFields,
    trigger,
    customFieldsCollection,
  }

  switch (action.attributes.actionable_type) {
    case Action.Types.requestReview:
      return <RequestReviewAction {...props} />

    case Action.Types.sendNotification:
      return <SendNotificationAction {...props} />

    case Action.Types.sendCustomNotification:
      return <SendCustomNotificationAction {...props} />

    case Action.Types.branch:
      return <BranchAction {...props} condition={automationsStore.stores[id]?.condition} />

    case Action.Types.time:
      return <TimeAction {...props} />

    default:
      return null
  }
}

const AutomationsShow = ({ automation, trigger, automationsStore }: Props) => {
  const [loading, setLoading] = React.useState(false)
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const actionList = automation ? actions.orderedListForAutomation(automation.id) : []

  const customFieldsCollection = useMemo(
    () =>
      new CustomFieldsCollection([], {
        model_ref: '',
        instance_id: '',
      }),
    []
  )

  const { loading: loadingCustomFields } = useAsyncData(
    () => customFieldsCollection.byAutomationsUsageGroup(),
    []
  )

  if (!automation) return null

  return (
    <CachedDataProvider setLoading={setLoading}>
      <Panes direction="horizontal-2-cols-left-1-third">
        <Box position="sticky" style={{ top: '56px', zIndex: '1000' }}>
          <Title automation={automation} toggleEnabled={() => automation.toggleActivation()} />
        </Box>

        <div className={styles.right_side}>
          <PanesPad>
            <div className={styles.centered}>
              {automation && automation.trigger && (
                <AutomationTrigger
                  disabled={automation.isDefault}
                  onClick={() => {
                    navigate(
                      navigation.settings.automations.id.details.addBlockParentId.fullPath({
                        id: automation.id,
                        parentId: 'trigger',
                      })
                    )
                  }}
                  title={automationTranslations(automation)}
                  trigger={automation.attributes.trigger}
                  withLastSpacer={actionList.length !== 0}
                />
              )}
              {actionList.map((action, index, actionsArray) => (
                <ActionSwitch
                  key={action.get('id')}
                  automation={automation}
                  automationsStore={automationsStore}
                  action={action}
                  index={index}
                  trigger={trigger}
                  actionsArray={actionsArray}
                  loadingCustomFields={loadingCustomFields || loading}
                  customFieldsCollection={customFieldsCollection}
                />
              ))}
            </div>
          </PanesPad>
        </div>
      </Panes>
    </CachedDataProvider>
  )
}

export default observer(AutomationsShow)
